import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import {TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getTestOrdersSearchUrl} from 'sample-tests/ui/utils/testOrderSearchUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';

import {PMEITestOrderQuery, PMEITestOrderQueryVariables} from './__generated/PMEITestOrderQuery';

export type TestOrderOrInaccessibleNode = ExcludeInaccessible<
  PMEITestOrderQuery['procedureMethodExecutionInstances'][number]
>['testOrders'][number];

export type TestOrderNode = ExcludeInaccessible<TestOrderOrInaccessibleNode>;

export type PMEINode = ExcludeInaccessible<PMEITestOrderQuery['procedureMethodExecutionInstances'][number]>;

export type PMEIWithStudyNode = PMEINode & {
  procedureRun: ExcludeInaccessible<PMEINode['procedureRun']> & {
    study: NonNullable<ExcludeInaccessible<PMEINode['procedureRun']['study']>>;
  };
};

interface WorksheetTestOrderUpdatesOnRetractionBannerProps {
  worksheetId: string;
}

export const PMEI_TEST_ORDER_QUERY = gql`
  query PMEITestOrderQuery($worksheetId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    procedureMethodExecutionInstances(worksheetId: $worksheetId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        testOrders {
          ... on TestOrderNode {
            id
            name
            status
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        createdRetestOrders {
          ... on TestOrderNode {
            id
            name
            status
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        procedureRun {
          ... on ProcedureRunNode {
            id
            study {
              ... on StudyNode {
                id
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function WorksheetTestOrderUpdatesOnRetractionBanner(
  props: WorksheetTestOrderUpdatesOnRetractionBannerProps
): JSX.Element | null {
  const pmeiResult = useBenchlingQuery<PMEITestOrderQuery, PMEITestOrderQueryVariables>(
    PMEI_TEST_ORDER_QUERY,
    {
      variables: {worksheetId: props.worksheetId},
    }
  );

  if (pmeiResult.type !== QueryResultType.SUCCESS) {
    return null;
  }

  // The user should have read access to the PMEIs + study if they can get to this modal, so this is for safety + type narrowing
  pmeiResult.data.procedureMethodExecutionInstances.forEach((pmei) => {
    assert(
      !isInaccessible(pmei) && pmei.procedureRun.study != null && !isInaccessible(pmei.procedureRun.study),
      'Access Denied.'
    );
  });

  return (
    <PresentationalWorksheetTestOrderUpdatesOnRetractionBanner
      pmeis={pmeiResult.data.procedureMethodExecutionInstances as Array<PMEIWithStudyNode>}
    />
  );
}

export function PresentationalWorksheetTestOrderUpdatesOnRetractionBanner({
  pmeis,
}: {
  pmeis: Array<PMEIWithStudyNode>;
}): JSX.Element | null {
  const studyId = pmeis[0].procedureRun.study.id;
  assert(
    pmeis.every((pmei) => pmei.procedureRun.study.id === studyId),
    'Expected all PMEIs to have the same study'
  );
  const allTestOrders = pmeis.flatMap((pmei) => pmei.testOrders);
  const allCreatedRetestOrders = pmeis.flatMap((pmei) => pmei.createdRetestOrders);
  if (allTestOrders.length === 0 && allCreatedRetestOrders.length === 0) {
    return null;
  }

  return (
    <div className={css(styles.banners)}>
      {allTestOrders.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'View test orders',
            actionHref: getTestOrdersSearchUrl(allTestOrders, studyId, [
              TestOrderStatus.COMPLETED,
              TestOrderStatus.INVALID,
            ]),
          }}
          text="Plated test orders that are completed or invalid will be updated."
          supportingInfo={
            <div>
              Cancelling reviews and approvals will update (up to) {allTestOrders.length} test orders to in
              progress and clear their results and analysis comments
            </div>
          }
        />
      )}
      {allCreatedRetestOrders.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'View retest orders',
            actionHref: getTestOrdersSearchUrl(allCreatedRetestOrders, studyId, [
              TestOrderStatus.PENDING,
              TestOrderStatus.BLOCKED,
            ]),
          }}
          text="Retest orders that are pending or blocked will be cancelled."
          supportingInfo={
            <div>
              Cancelling reviews and approvals will cancel (up to) {allCreatedRetestOrders.length} retest
              orders
            </div>
          }
        />
      )}
    </div>
  );
}

export default WorksheetTestOrderUpdatesOnRetractionBanner;

const styles = StyleSheet.create({
  banners: {
    paddingBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});
